import type { Shape } from "@gettactic/map-builder/src/types";
import { getShapeBounds } from "@gettactic/map-builder/src/utils/snap";
import type { Point, Rect } from "../types";

/**
 * World-space axis-aligned bounds of a shape, rotation included. The
 * stored-coordinate bounds from `snap.ts` are reused for the unrotated
 * case so selection outlines, snapping and culling agree on one box.
 */
export const getShapeAabb = (shape: Shape): Rect => {
  const bounds = getShapeBounds(shape);
  const rotation = getShapeRotation(shape);
  const box = {
    x: bounds.left,
    y: bounds.top,
    width: bounds.right - bounds.left,
    height: bounds.bottom - bounds.top
  };
  if (rotation === 0) return box;
  // Every box-anchored shape (rectangle, spot, circle, …) rotates about
  // its origin (x, y); the world box is the rotated local box.
  const pivot = { x: shape.x, y: shape.y };
  return unionPoints(
    [
      { x: box.x, y: box.y },
      { x: box.x + box.width, y: box.y },
      { x: box.x + box.width, y: box.y + box.height },
      { x: box.x, y: box.y + box.height }
    ].map((corner) => rotatePoint(corner, pivot, rotation))
  );
};

/** The shape's box in its own unrotated frame (stored coordinates). */
export const getShapeLocalBox = (shape: Shape): Rect => {
  const bounds = getShapeBounds(shape);
  return {
    x: bounds.left,
    y: bounds.top,
    width: bounds.right - bounds.left,
    height: bounds.bottom - bounds.top
  };
};

/** Shapes drawn from a box rotate about their origin (x, y); walls, wall
 *  paths and groups keep their geometry in world space and never rotate. */
export const getShapeRotation = (shape: Shape): number =>
  shape.type === "wall" || shape.type === "wall-path" || shape.type === "group"
    ? 0
    : shape.rotation || 0;

export const rotatePoint = (
  point: Point,
  pivot: Point,
  degrees: number
): Point => {
  if (degrees === 0) return point;
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  return { x: pivot.x + cos * dx - sin * dy, y: pivot.y + sin * dx + cos * dy };
};

/** World point → the shape's unrotated frame. */
export const toShapeLocal = (shape: Shape, world: Point): Point =>
  rotatePoint(world, { x: shape.x, y: shape.y }, -getShapeRotation(shape));

/** Point in the shape's unrotated frame → world. */
export const fromShapeLocal = (shape: Shape, local: Point): Point =>
  rotatePoint(local, { x: shape.x, y: shape.y }, getShapeRotation(shape));

/** SVG transform that takes the shape's unrotated frame to the world. */
export const shapeFrameTransform = (shape: Shape): string | undefined => {
  const rotation = getShapeRotation(shape);
  return rotation ? `rotate(${rotation} ${shape.x} ${shape.y})` : undefined;
};

export const unionPoints = (points: Point[]): Rect => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export const unionRects = (rects: Rect[]): Rect | null => {
  if (rects.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const rect of rects) {
    if (rect.x < minX) minX = rect.x;
    if (rect.y < minY) minY = rect.y;
    if (rect.x + rect.width > maxX) maxX = rect.x + rect.width;
    if (rect.y + rect.height > maxY) maxY = rect.y + rect.height;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export const rectsIntersect = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;

export const rectContainsPoint = (rect: Rect, point: Point): boolean =>
  point.x >= rect.x &&
  point.x <= rect.x + rect.width &&
  point.y >= rect.y &&
  point.y <= rect.y + rect.height;

export const normalizeRect = (from: Point, to: Point): Rect => ({
  x: Math.min(from.x, to.x),
  y: Math.min(from.y, to.y),
  width: Math.abs(to.x - from.x),
  height: Math.abs(to.y - from.y)
});

/**
 * Bounds of several shapes in a frame rotated by `rotation` degrees: the
 * box is computed in the unrotated frame (corners turned back by the
 * angle about a fixed pivot) and drawn with `frameTransform`.
 */
export const getFrameBox = (
  shapes: Shape[],
  rotation: number
): { box: Rect; pivot: Point; transform?: string } | null => {
  const world = unionRects(shapes.map(getShapeAabb));
  if (!world) return null;
  const pivot = { x: world.x + world.width / 2, y: world.y + world.height / 2 };
  if (!rotation) return { box: world, pivot };
  const corners: Point[] = [];
  for (const shape of shapes) {
    const local = getShapeLocalBox(shape);
    const shapeRotation = getShapeRotation(shape);
    const shapePivot = { x: shape.x, y: shape.y };
    for (const corner of [
      { x: local.x, y: local.y },
      { x: local.x + local.width, y: local.y },
      { x: local.x + local.width, y: local.y + local.height },
      { x: local.x, y: local.y + local.height }
    ]) {
      const inWorld = rotatePoint(corner, shapePivot, shapeRotation);
      corners.push(rotatePoint(inWorld, pivot, -rotation));
    }
  }
  return {
    box: unionPoints(corners),
    pivot,
    transform: `rotate(${rotation} ${pivot.x} ${pivot.y})`
  };
};
